<script setup lang="ts">
import { computed, ref, toRef, watch } from "vue";
import { useRoute } from "vue-router";
import { useDialogFocus } from "@/composables/useDialogFocus";

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

const route = useRoute();
const toast = ref<{ text: string; kind: "ok" | "err" } | null>(null);
const qrMode = ref(false);
const dialogRef = ref<HTMLElement | null>(null);
let toastTimer: ReturnType<typeof setTimeout> | null = null;

function requestClose(): void {
  emit("close");
}

useDialogFocus(toRef(props, "open"), dialogRef, requestClose);

const pageUrl = computed(() => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return origin + route.fullPath;
});

const pageTitle = computed(() => {
  const map: Record<string, string> = {
    nebula: "江城星忆 · 3D 知识图谱星云",
    map: "武汉历史建筑文化地图",
    "memory-grid-insight": "记忆网格洞察分析",
    "building-detail": "建筑档案详情",
    "building-reposition": "旧影重绘",
    "photo-reposition": "旧影重绘",
    crowdsource: "文化记忆共建",
    growth: "江城生长 · 武汉城市时空演变",
    "old-photos": "江城旧影 · 历史照片档案",
    "crowdsource-submit": "共建投稿",
    "contribution-browse": "共建浏览",
    "contribution-detail": "投稿详情",
    "crowd-map": "共建地图",
    "archive-review": "档案审核",
    "spatial-stats": "文化记忆空间诊断"
  };
  const name = String(route.name || "");
  return map[name] || (typeof document !== "undefined" ? document.title : "江城星忆");
});

const pageSummary = computed(() => {
  if (route.name === "memory-grid-insight") {
    const level = route.params.level;
    const gridId = route.params.gridId;
    return `${level === "L1" ? "1000米" : "200米"}网格 ${gridId} 的空间诊断报告`;
  }
  if (route.name === "building-detail") {
    return `查看 ${String(route.params.buildingId)} 的建筑档案`;
  }
  return "武汉历史建筑文化遗产数字保护与公众参与平台";
});

interface ShareItem {
  key: string;
  label: string;
  img?: string;
  svg?: "copy" | "share";
  action: () => void;
}

const shareItems = computed<ShareItem[]>(() => {
  const items: ShareItem[] = [
    { key: "wechat", label: "微信", img: "/images/wechat.png", action: openWechatQr },
    { key: "qq", label: "QQ", img: "/images/QQ.png", action: shareToQQ },
    { key: "weibo", label: "微博", img: "/images/weibo.png", action: shareToWeibo },
    { key: "copy", label: "复制链接", svg: "copy", action: copyLink }
  ];

  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    items.push({ key: "system", label: "系统分享", svg: "share", action: systemShare });
  }

  return items;
});

const qrCodeUrl = computed(() => {
  const data = encodeURIComponent(pageUrl.value);
  return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${data}&margin=8`;
});

function showToastMessage(text: string, kind: "ok" | "err") {
  toast.value = { text, kind };
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.value = null; }, 2200);
}

function openWechatQr() { qrMode.value = true; }
function backFromQr() { qrMode.value = false; }

function shareToQQ() {
  const url = new URL("https://connect.qq.com/widget/shareqq/index.html");
  url.searchParams.set("url", pageUrl.value);
  url.searchParams.set("title", pageTitle.value);
  url.searchParams.set("summary", pageSummary.value);
  url.searchParams.set("desc", pageSummary.value);
  url.searchParams.set("site", "江城星忆");
  window.open(url.toString(), "_blank", "noopener,noreferrer");
  emit("close");
}

function shareToWeibo() {
  const url = new URL("https://service.weibo.com/share/share.php");
  url.searchParams.set("url", pageUrl.value);
  url.searchParams.set("title", `${pageTitle.value} — 江城星忆`);
  url.searchParams.set("appkey", "");
  url.searchParams.set("searchPic", "false");
  window.open(url.toString(), "_blank", "noopener,noreferrer");
  emit("close");
}

async function copyLink() {
  try {
    await navigator.clipboard.writeText(pageUrl.value);
    showToastMessage("链接已复制到剪贴板", "ok");
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = pageUrl.value;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    try { document.execCommand("copy"); showToastMessage("链接已复制", "ok"); }
    catch { showToastMessage("复制失败，请手动复制链接", "err"); }
    document.body.removeChild(textarea);
  }
}

async function systemShare() {
  try {
    await navigator.share({ title: pageTitle.value, text: pageSummary.value, url: pageUrl.value });
    emit("close");
  } catch { /* cancelled */ }
}

watch(() => props.open, (open) => {
  if (!open) { toast.value = null; qrMode.value = false; }
});

const copySvgPath = "M8 16H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2m-6 12h8a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-8a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2z";
const shareSvgPath = "M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13";
</script>

<template>
  <Teleport to="body">
    <Transition name="auth-fade">
      <div v-if="open" class="auth-backdrop" @click.self="requestClose">
        <div ref="dialogRef" class="auth-card share-card" role="dialog" aria-modal="true" aria-label="分享当前页面" tabindex="-1">

          <!-- Toast -->
          <Transition name="toast-slide">
            <div v-if="toast" :class="['auth-toast', toast.kind === 'ok' ? 'toast-ok' : 'toast-err']">
              <span class="toast-dot" />{{ toast.text }}
            </div>
          </Transition>

          <button class="auth-card-close" type="button" @click="requestClose" aria-label="关闭分享窗口">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>

          <!-- QR Code mode (WeChat) -->
          <template v-if="qrMode">
            <div class="share-qr-view">
              <button class="share-back-btn" type="button" @click="backFromQr">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                返回
              </button>
              <img class="share-qr-brand" src="/images/wechat.png" alt="微信" width="40" height="40" />
              <h3>微信扫一扫分享</h3>
              <p>打开手机微信"扫一扫"，扫描二维码即可分享给好友或朋友圈</p>
              <div class="share-qr-code">
                <img :src="qrCodeUrl" alt="微信分享二维码" width="220" height="220" />
              </div>
              <small class="share-qr-url">{{ pageUrl }}</small>
            </div>
          </template>

          <!-- Main share bar -->
          <template v-else>
            <div class="share-header">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
              <span>分享</span>
            </div>
            <p class="share-subtitle">{{ pageTitle }}</p>

            <div class="share-row">
              <button
                v-for="item in shareItems"
                :key="item.key"
                class="share-item"
                type="button"
                @click="item.action"
              >
                <span class="share-item-icon">
                  <img v-if="item.img" :src="item.img" :alt="item.label" width="32" height="32" />
                  <svg v-else-if="item.svg === 'copy'" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path :d="copySvgPath" /></svg>
                  <svg v-else width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path :d="shareSvgPath" /></svg>
                </span>
                <span class="share-item-label">{{ item.label }}</span>
              </button>
            </div>

            <p class="share-url-hint">{{ pageUrl }}</p>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
